import { useEffect, useMemo, useState } from 'react';
import { getMissedVisitOptionsVisitsMissedVisitOptionsGet } from '../services/api/sdk.gen';
import type {
  MissedVisitReason,
  MissedVisitReasonOption,
  NotificationMethod,
  NotificationMethodOption,
} from '../services/api/types.gen';

/**
 * Reason + notification-method options for the missed-visit form, fetched from
 * `GET /visits/missed-visit-options`. The labels are owned by the backend
 * (`MISSED_VISIT_REASON_LABELS` / `NOTIFICATION_METHOD_LABELS` in
 * `app.models.enum.visit`) and shared with the missed-visit print view, so the
 * on-screen form and the printed PDF render identical copy with no duplicated
 * mapping on the frontend.
 */
export type MissedVisitOptions = {
  /** `[value, label]` pairs in backend-defined display order. */
  reasonOptions: [MissedVisitReason, string][];
  notificationMethodOptions: [NotificationMethod, string][];
  /** `value → label` lookup for rendering stored values (read-only view). */
  reasonLabels: Partial<Record<MissedVisitReason, string>>;
  notificationMethodLabels: Partial<Record<NotificationMethod, string>>;
  loading: boolean;
  /** True when the fetch failed; the edit form can't be completed without options. */
  error: boolean;
};

type Fetched = {
  reasons: MissedVisitReasonOption[];
  notificationMethods: NotificationMethodOption[];
};

const EMPTY: Fetched = { reasons: [], notificationMethods: [] };

// Module-level cache: the labels are process-stable, so fetch once and share
// across every MissedVisitForm instance (mirrors useVisitTypeOptions).
let cached: Fetched | null = null;
let fetchPromise: Promise<Fetched> | null = null;

async function fetchOptions(): Promise<Fetched> {
  if (cached) return cached;
  if (fetchPromise) return fetchPromise;

  fetchPromise = getMissedVisitOptionsVisitsMissedVisitOptionsGet({})
    .then(res => {
      const data = res.data;
      if (!data)
        throw new Error('Unexpected missed-visit options payload shape');
      cached = {
        reasons: data.reasons,
        notificationMethods: data.notification_methods,
      };
      return cached;
    })
    .catch(err => {
      // Reset so a later mount can retry; surface the failure to callers so
      // the form can degrade visibly rather than showing empty dropdowns.
      fetchPromise = null;
      console.error('Failed to load missed-visit options', err);
      throw err;
    });

  return fetchPromise;
}

export function useMissedVisitOptions(): MissedVisitOptions {
  const [fetched, setFetched] = useState<Fetched>(cached ?? EMPTY);
  const [loading, setLoading] = useState(!cached);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (cached) {
      setFetched(cached);
      setLoading(false);
      return;
    }
    let cancelled = false;
    fetchOptions()
      .then(data => {
        if (!cancelled) {
          setFetched(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setFetched(EMPTY);
          setLoading(false);
          setError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return useMemo(() => {
    const reasonOptions = fetched.reasons.map(
      o => [o.value, o.label] as [MissedVisitReason, string]
    );
    const notificationMethodOptions = fetched.notificationMethods.map(
      o => [o.value, o.label] as [NotificationMethod, string]
    );
    return {
      reasonOptions,
      notificationMethodOptions,
      reasonLabels: Object.fromEntries(reasonOptions) as Partial<
        Record<MissedVisitReason, string>
      >,
      notificationMethodLabels: Object.fromEntries(
        notificationMethodOptions
      ) as Partial<Record<NotificationMethod, string>>,
      loading,
      error,
    };
  }, [fetched, loading, error]);
}
